/**
 * `PhoneInput` — controlled country-code + national-number input.
 *
 * Wraps the shadcn-`Input` look with a left-side dial-code picker (flag +
 * `+code` in the trigger, full country name in the menu) and a numeric input.
 * The picker is a self-contained popover (no Radix dependency) so the input
 * works inside RHF `register('phone')` with a single combined string value —
 * matching the legacy CRA payload from `react-phone-input-2` where the saved
 * value looked like `+919876543210`.
 *
 * Wire-up: caller passes `value` (e.g. `user.phone`) + `onChange(next)`. We
 * keep internal state for the national digits + selected country so the user
 * can type uninterrupted even if the caller doesn't immediately echo `value`
 * back; an externally-changed `value` prop reseeds the internal state.
 *
 * The country list is searchable by name (case-insensitive substring) so users
 * don't have to scroll past 100+ countries to find theirs.
 */
import {
  forwardRef,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronDown, Search } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  DEFAULT_PHONE_COUNTRY,
  PHONE_COUNTRIES,
  combinePhoneValue,
  findCountryByCode,
  splitPhoneValue,
  type PhoneCountry,
} from './phone-countries';

export interface PhoneInputProps {
  /** Combined value — `+<dial><national>` (the legacy backend shape). */
  value?: string;
  /** Fired whenever the dial code OR the national number changes. */
  onChange?: (next: string) => void;
  /** Optional id for the underlying number input (used by `<label htmlFor>`). */
  id?: string;
  /** Optional name for the number input (lets RHF `register()` interop work). */
  name?: string;
  /** Read-only mode — both the picker and the input are disabled. */
  readOnly?: boolean;
  /** Disable the entire control. */
  disabled?: boolean;
  /** Placeholder for the national-number input. */
  placeholder?: string;
  /** Wrapper className. */
  className?: string;
  /** ARIA: passes through to the national-number input. */
  'aria-invalid'?: boolean;
  /** ARIA: passes through to the national-number input. */
  'aria-describedby'?: string;
  /**
   * ISO-2 country code (`'US'`, `'IN'`, `'GB'`) used as the default
   * dial-code when `value` is empty / unparseable. Typically wired to
   * `user.country` so a US user defaults to `+1` instead of `+91`.
   * Falls back to {@link DEFAULT_PHONE_COUNTRY} (India) when omitted or
   * the code is unknown.
   */
  defaultCountryCode?: string | null;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  function PhoneInput(
    {
      value,
      onChange,
      id,
      name,
      readOnly,
      disabled,
      placeholder = 'Phone number',
      className,
      'aria-invalid': ariaInvalid,
      'aria-describedby': ariaDescribedBy,
      defaultCountryCode,
    },
    ref,
  ) {
    // Resolve the fallback country once per render: if the caller passed a
    // known ISO code, use it; otherwise India (legacy default).
    const fallbackCountry = useMemo(
      () => findCountryByCode(defaultCountryCode) ?? DEFAULT_PHONE_COUNTRY,
      [defaultCountryCode],
    );

    // Seed internal state from the incoming value, but maintain it locally so
    // an uncontrolled (or partially-controlled) parent doesn't lose the user's
    // keystrokes between renders. Empty `value` → use the fallback country.
    const initial = useMemo(() => {
      const split = splitPhoneValue(value);
      if (!split.national && (!value || String(value).trim() === '')) {
        return { country: fallbackCountry, national: '' };
      }
      return split;
    }, [value, fallbackCountry]);
    const [country, setCountry] = useState<PhoneCountry>(initial.country);
    const [national, setNational] = useState<string>(initial.national);

    // Track the last `value` prop we synced from so an *external* update
    // (e.g. `form.reset()` or a stored profile arriving) re-seeds the input,
    // but a local keystroke doesn't get clobbered by our own re-emit.
    const lastValueRef = useRef<string | undefined>(value);
    useEffect(() => {
      if (value === lastValueRef.current) return;
      lastValueRef.current = value;
      const next = splitPhoneValue(value);
      // If the incoming value is empty, honour the caller's default country
      // instead of forcing the global India default.
      if (!next.national && (!value || String(value).trim() === '')) {
        setCountry(fallbackCountry);
        setNational('');
        return;
      }
      setCountry(next.country);
      setNational(next.national);
    }, [value, fallbackCountry]);

    // If `defaultCountryCode` changes while the field is empty (e.g. the user
    // picks a new city on the form and we want the dial to update too),
    // mirror the new fallback into the trigger.
    useEffect(() => {
      if (national) return;
      setCountry((prev) =>
        prev.code === fallbackCountry.code ? prev : fallbackCountry,
      );
    }, [fallbackCountry, national]);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listboxId = useId();

    // Close the popover when the user clicks outside the wrapper.
    useEffect(() => {
      if (!open) return;
      function onDocClick(e: MouseEvent) {
        if (!wrapperRef.current) return;
        if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
      }
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    function emit(nextCountry: PhoneCountry, nextNational: string) {
      const combined = combinePhoneValue(nextCountry, nextNational);
      lastValueRef.current = combined;
      onChange?.(combined);
    }

    function chooseCountry(c: PhoneCountry) {
      setOpen(false);
      setQuery('');
      setCountry(c);
      // Keep the national number the user already typed.
      emit(c, national);
    }

    function onNationalChange(e: React.ChangeEvent<HTMLInputElement>) {
      // Strip everything that isn't a digit — the dial code lives in the
      // dropdown, not the input, so users can't accidentally type a "+" or
      // spaces and end up with a malformed payload.
      const digits = e.target.value.replace(/[^\d]/g, '');
      setNational(digits);
      emit(country, digits);
    }

    const filtered = useMemo(() => {
      if (!query.trim()) return PHONE_COUNTRIES;
      const q = query.trim().toLowerCase();
      return PHONE_COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dial.includes(q) ||
          c.code.toLowerCase().includes(q),
      );
    }, [query]);

    return (
      <div
        ref={wrapperRef}
        className={cn(
          'relative flex w-full items-stretch rounded-md border border-border bg-surface focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
      >
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label={`Country code, currently ${country.name} ${country.dial}`}
          disabled={disabled || readOnly}
          onClick={() => setOpen((s) => !s)}
          className={cn(
            'flex shrink-0 items-center gap-1.5 rounded-l-md border-r border-border bg-surface-2 px-2.5 text-sm text-ink transition-colors hover:bg-surface-2/80 focus:outline-none',
            (disabled || readOnly) && 'cursor-not-allowed',
          )}
        >
          <span aria-hidden="true" className="text-base leading-none">
            {country.flag}
          </span>
          <span className="font-medium tabular-nums">{country.dial}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </button>

        <input
          ref={ref}
          id={id}
          name={name}
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          value={national}
          onChange={onNationalChange}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          className={cn(
            'flex h-10 w-full min-w-0 rounded-r-md bg-surface px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed',
          )}
        />

        {open ? (
          <div
            className="absolute left-0 top-full z-30 mt-1 w-72 max-w-[90vw] overflow-hidden rounded-md border border-border bg-surface shadow-lg"
          >
            <div className="flex items-center gap-2 border-b border-border bg-surface-2 px-2 py-2">
              <Search
                className="h-3.5 w-3.5 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country…"
                aria-label="Search countries"
                className="h-7 w-full bg-transparent text-sm text-ink placeholder:text-muted-foreground focus:outline-none"
                // Don't autofocus — JSDOM tests run the dropdown without
                // user interaction; let the caller drive focus instead.
              />
            </div>
            <ul
              id={listboxId}
              role="listbox"
              aria-label="Country dial codes"
              className="max-h-64 overflow-auto py-1"
            >
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-xs text-muted-foreground">
                  No matches
                </li>
              ) : (
                filtered.map((c) => {
                  const selected = c.code === country.code;
                  // role="option" lives on the button itself so RTL's
                  // getByRole('option', { name: ... }) returns the button
                  // (and a click event therefore lands on it directly).
                  return (
                    <li key={c.code}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => chooseCountry(c)}
                        // Also handle mousedown so a click inside an input
                        // doesn't fire onBlur first and unmount the popover.
                        onMouseDown={(e) => e.preventDefault()}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2',
                          selected && 'bg-surface-2 font-medium',
                        )}
                      >
                        <span aria-hidden="true" className="text-base leading-none">
                          {c.flag}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{c.name}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {c.dial}
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        ) : null}
      </div>
    );
  },
);
